/**
 * Topic catalog for semantic worksheet-column routing.
 *
 * Each row is a subject the agent can resolve once per company row. Rows are
 * tagged with the workload that owns them so a terse request lands on the
 * right lifecycle: `tabular` for research and fundamentals, `id_lookup` for
 * the five identity fields FactSet's ID Lookup answers authoritatively, and
 * `none` for requests that mention a topic word while asking for something
 * else entirely.
 *
 * The `none` rows replace what used to be veto regexes. "Bold the revenue
 * column" mentions a catalog topic but is a formatting request; it matches a
 * formatting row far more closely than the Revenue row, so the router declines
 * without needing a hand-maintained list of forbidden verbs.
 *
 * Aliases are the terse forms users actually type. They are embedded
 * individually rather than concatenated, and a topic scores as its
 * best-matching alias — a one-word request matches a one-word alias cleanly
 * instead of being diluted against a paragraph.
 */

import type { IdLookupRequestedField } from "./idLookupRoutingPolicy";

export type WorksheetTopicOwner = "tabular" | "id_lookup" | "none";

export interface WorksheetTopicRow {
  key: string;
  name: string;
  category: string;
  aliases: readonly string[];
  owner: WorksheetTopicOwner;
  /** Field to request when this row routes to the ID Lookup workload. */
  idLookupField?: IdLookupRequestedField;
}

function tabular(
  key: string,
  name: string,
  category: string,
  aliases: readonly string[] = [],
): WorksheetTopicRow {
  return { key, name, category, aliases, owner: "tabular" };
}

const MARKET_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("52_week_high", "52-Week High", "market data", [
    "52 week high",
    "yearly high",
    "one year high",
    "highest price in the last year",
  ]),
  tabular("52_week_low", "52-Week Low", "market data", [
    "52 week low",
    "yearly low",
    "one year low",
    "lowest price in the last year",
  ]),
  tabular("share_price", "Share Price", "market data", [
    "stock price",
    "closing price",
    "prior close",
    "previous close",
    "last close",
    "yesterday's close",
    "prior day close",
    "latest closing price",
    "last trading day price",
    "price per share",
    "current price",
  ]),
  tabular("consensus_target_price", "Consensus Target Price", "market data", [
    "target price",
    "analyst target price",
    "price target",
  ]),
  tabular("market_cap", "Market Cap", "market data", [
    "market capitalization",
    "mkt cap",
    "how big is the company by market value",
  ]),
  tabular("total_return", "Total Return", "market data", [
    "shareholder return",
    "total shareholder return",
    "tsr",
  ]),
  tabular("free_float", "Free Float", "market data", [
    "float",
    "publicly traded shares",
  ]),
];

const VALUATION_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("ev_ebitda", "EV/EBITDA", "valuation", [
    "enterprise value to ebitda",
    "ev to ebitda multiple",
  ]),
  tabular("ev_ebit", "EV/EBIT", "valuation", ["enterprise value to ebit"]),
  tabular("ev_fcf", "EV/FCF", "valuation", [
    "enterprise value to free cash flow",
  ]),
  tabular("ev_sales", "EV/Sales", "valuation", [
    "enterprise value to sales",
    "enterprise value to revenue",
  ]),
  tabular("pe", "P/E", "valuation", [
    "price to earnings",
    "pe ratio",
    "earnings multiple",
  ]),
  tabular("pb", "P/B", "valuation", ["price to book", "pb ratio"]),
  tabular("ps", "P/S", "valuation", ["price to sales", "ps ratio"]),
  tabular("pfcf", "P/FCF", "valuation", ["price to free cash flow"]),
];

const PER_SHARE_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("book_value_share", "Book Value / Share", "per share", [
    "book value per share",
    "bvps",
  ]),
  tabular(
    "book_value_share_growth",
    "Book Value / Share Growth",
    "per share",
    ["book value per share growth"],
  ),
  tabular("cash_flow_share_growth", "Cash Flow / Share Growth", "per share", [
    "cash flow per share growth",
  ]),
  tabular("revenue_share_growth", "Revenue / Share Growth", "per share", [
    "revenue per share growth",
    "sales per share growth",
  ]),
  tabular("eps", "EPS", "per share", [
    "earnings per share",
    "diluted eps",
  ]),
  tabular("eps_growth", "EPS Growth", "per share", [
    "earnings per share growth",
  ]),
  tabular("eps_lt_growth", "EPS Long-Term Growth", "per share", [
    "long term eps growth",
    "long term earnings growth rate",
  ]),
  tabular("shares_outstanding", "Shares Outstanding", "per share", [
    "share count",
    "number of shares",
    "diluted shares",
  ]),
];

const SHAREHOLDER_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("dividend_share", "Dividend / Share", "shareholder returns", [
    "dividend per share",
    "dps",
  ]),
  tabular(
    "dividend_share_growth",
    "Dividend / Share Growth",
    "shareholder returns",
    ["dividend per share growth", "dividend growth"],
  ),
  tabular("dividend_yield", "Dividend Yield", "shareholder returns", [
    "yield on the dividend",
  ]),
  tabular("dividend_policy", "Dividend Policy", "shareholder returns", [
    "what is the dividend policy",
  ]),
  tabular("payout_ratio", "Payout Ratio", "shareholder returns", [
    "dividend payout ratio",
  ]),
  tabular("share_repurchases", "Share Repurchases", "shareholder returns", [
    "buybacks",
    "stock buybacks",
    "shares repurchased",
  ]),
  tabular(
    "buyback_authorisation",
    "Buyback Authorisation",
    "shareholder returns",
    ["buyback authorization", "repurchase program size"],
  ),
  tabular("cash_dividends_paid", "Cash Dividends Paid", "shareholder returns", [
    "dividends paid in cash",
  ]),
  tabular(
    "cash_dividend_coverage",
    "Cash Dividend Coverage",
    "shareholder returns",
    ["dividend coverage ratio"],
  ),
  tabular(
    "cash_dividends_cash_flow",
    "Cash Dividends / Cash Flow",
    "shareholder returns",
    ["dividends as a share of cash flow"],
  ),
];

const INCOME_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("revenue", "Revenue", "income statement", [
    "sales",
    "turnover",
    "top line",
    "latest revenue",
    "quarterly revenue",
    "last quarter revenue",
    "lq revenue",
    "most recent quarter sales",
  ]),
  tabular("revenue_growth", "Revenue Growth", "income statement", [
    "sales growth",
    "top line growth",
  ]),
  tabular(
    "organic_revenue_growth",
    "Organic Revenue Growth",
    "income statement",
    ["organic growth", "like for like growth"],
  ),
  tabular("revenue_by_segment", "Revenue by Segment", "income statement", [
    "segment revenue",
    "revenue split by segment",
  ]),
  tabular("gross_profit", "Gross Profit", "income statement", []),
  tabular("gross_margin", "Gross Margin", "income statement", [
    "gross profit margin",
  ]),
  tabular("ebit", "EBIT", "income statement", [
    "earnings before interest and tax",
  ]),
  tabular("ebit_margin", "EBIT Margin", "income statement", []),
  tabular("ebitda", "EBITDA", "income statement", [
    "earnings before interest tax depreciation and amortization",
  ]),
  tabular("ebitda_margin", "EBITDA Margin", "income statement", []),
  tabular("operating_income", "Operating Income", "income statement", [
    "operating profit",
  ]),
  tabular(
    "operating_income_growth",
    "Operating Income Growth",
    "income statement",
    ["operating profit growth"],
  ),
  tabular("operating_margin", "Operating Margin", "income statement", []),
  tabular("pretax_income", "Pretax Income", "income statement", [
    "profit before tax",
    "income before taxes",
  ]),
  tabular("pretax_margin", "Pretax Margin", "income statement", []),
  tabular("net_income", "Net Income", "income statement", [
    "net profit",
    "bottom line",
    "earnings",
  ]),
  tabular("net_income_growth", "Net Income Growth", "income statement", [
    "earnings growth",
  ]),
  tabular("net_margin", "Net Margin", "income statement", [
    "net profit margin",
  ]),
  tabular("net_margin_growth", "Net Margin Growth", "income statement", []),
];

const TAX_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("effective_tax_rate", "Effective Tax Rate", "tax", ["tax rate"]),
  tabular("tax_expense", "Tax Expense", "tax", ["income tax expense"]),
  tabular("current_tax_expense", "Current Tax Expense", "tax", []),
  tabular("deferred_tax_expense", "Deferred Tax Expense", "tax", []),
  tabular("income_tax_payable", "Income Tax Payable", "tax", []),
  tabular("cash_taxes_paid", "Cash Taxes Paid", "tax", ["taxes paid in cash"]),
];

const CASH_FLOW_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("capex", "Capex", "cash flow", [
    "capital expenditure",
    "capital spending",
  ]),
  tabular("capex_guidance", "Capex Guidance", "cash flow", [
    "capital expenditure guidance",
    "guided capex",
    "capex outlook",
  ]),
  tabular("operating_cash_flow", "Operating Cash Flow", "cash flow", [
    "cash from operations",
    "ocf",
  ]),
  tabular("free_cash_flow", "Free Cash Flow", "cash flow", ["fcf"]),
  tabular("free_cash_flow_yield", "Free Cash Flow Yield", "cash flow", [
    "fcf yield",
  ]),
  tabular("cash_flow_roic", "Cash Flow ROIC", "cash flow", []),
  tabular("cash_interest_paid", "Cash Interest Paid", "cash flow", [
    "interest paid in cash",
  ]),
  tabular("gross_cash_flow_debt", "Gross Cash Flow / Debt", "cash flow", []),
  tabular("net_cash_flow_debt", "Net Cash Flow / Debt", "cash flow", []),
];

const BALANCE_SHEET_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("total_assets", "Total Assets", "balance sheet", []),
  tabular("total_assets_growth", "Total Assets Growth", "balance sheet", []),
  tabular("total_liabilities", "Total Liabilities", "balance sheet", []),
  tabular("total_equity", "Total Equity", "balance sheet", []),
  tabular("shareholders_equity", "Shareholders Equity", "balance sheet", [
    "shareholder equity",
    "stockholders equity",
  ]),
  tabular("current_assets", "Current Assets", "balance sheet", []),
  tabular(
    "cash_and_st_investments",
    "Cash & Short-Term Investments",
    "balance sheet",
    ["cash and equivalents", "cash on hand", "cash balance"],
  ),
  tabular("total_debt", "Total Debt", "balance sheet", ["debt", "borrowings"]),
  tabular("net_debt", "Net Debt", "balance sheet", []),
  tabular("short_term_debt", "Short-Term Debt", "balance sheet", [
    "current portion of debt",
  ]),
  tabular("long_term_debt", "Long-Term Debt", "balance sheet", []),
  tabular("debt_issued", "Debt Issued", "balance sheet", [
    "new debt raised",
  ]),
  tabular("debt_repaid", "Debt Repaid", "balance sheet", ["debt repayment"]),
];

const RATIO_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("current_ratio", "Current Ratio", "liquidity", []),
  tabular("quick_ratio", "Quick Ratio", "liquidity", ["acid test ratio"]),
  tabular("debt_equity", "Debt / Equity", "leverage", [
    "debt to equity",
    "gearing",
  ]),
  tabular("total_debt_ebitda", "Total Debt / EBITDA", "leverage", [
    "debt to ebitda",
    "leverage ratio",
    "net leverage",
  ]),
  tabular("return_on_equity", "Return on Equity", "returns", ["roe"]),
  tabular(
    "return_on_common_equity",
    "Return on Common Equity",
    "returns",
    ["roce on common equity"],
  ),
  tabular("return_on_assets", "Return on Assets", "returns", ["roa"]),
  tabular(
    "return_on_invested_capital",
    "Return on Invested Capital",
    "returns",
    ["roic"],
  ),
];

const COMPANY_TOPICS: readonly WorksheetTopicRow[] = [
  tabular("ceo", "CEO", "management", [
    "chief executive",
    "chief executive officer",
    "current ceo",
    "current chief executive",
    "who runs the company",
    "who is in charge",
    "head of the company",
  ]),
  tabular("cfo", "CFO", "management", [
    "chief financial officer",
    "finance chief",
    "current cfo",
  ]),
  tabular("auditor", "Auditor", "governance", [
    "audit firm",
    "who audits the company",
    "external auditor",
  ]),
  tabular("employee_count", "Employee Count", "company profile", [
    "headcount",
    "number of employees",
    "how many employees",
    "workforce size",
    "total employees",
    "staff count",
  ]),
  tabular("headquarters", "Headquarters", "company profile", [
    "hq",
    "head office",
    "headquarters location",
    "which city is it headquartered in",
  ]),
  tabular("business_description", "Business Description", "company profile", [
    "what does the company do",
    "company overview",
    "describe the business",
  ]),
  tabular("fiscal_year_end", "Fiscal Year End", "company profile", [
    "financial year end",
    "when does the fiscal year end",
  ]),
  tabular(
    "largest_business_segment",
    "Largest Business Segment",
    "company profile",
    ["biggest segment", "main business line"],
  ),
  tabular(
    "largest_region_by_revenue",
    "Largest Region by Revenue",
    "company profile",
    ["biggest region", "main geography by sales"],
  ),
  tabular("acquisitions", "Acquisitions", "corporate activity", [
    "recent acquisitions",
    "companies acquired",
    "m&a activity",
  ]),
  tabular("legal_proceedings", "Legal Proceedings", "risk", [
    "lawsuits",
    "litigation",
    "pending legal matters",
  ]),
  tabular("key_risk_factors", "Key Risk Factors", "risk", [
    "risk factors",
    "main risks",
  ]),
  tabular("strategic_priorities", "Strategic Priorities", "strategy", [
    "strategy",
    "key priorities",
    "strategic focus",
  ]),
  tabular("demand_outlook", "Demand Outlook", "guidance", [
    "outlook for demand",
  ]),
  tabular("margin_outlook", "Margin Outlook", "guidance", []),
  tabular("margin_guidance", "Margin Guidance", "guidance", [
    "guided margin",
  ]),
  tabular("earnings_call_tone", "Earnings-Call Tone", "qualitative", [
    "tone of the earnings call",
    "management sentiment",
  ]),
];

/**
 * Identity fields the FactSet ID Lookup workload answers authoritatively.
 * These stay off the research path even when phrased tersely — the lookup is
 * faster and its answers are canonical.
 */
const IDENTITY_TOPICS: readonly WorksheetTopicRow[] = [
  {
    key: "ultimate_parent",
    name: "Ultimate Parent Company",
    category: "identity",
    owner: "id_lookup",
    idLookupField: "ultimate_parent_name",
    aliases: [
      "parent company",
      "parent co",
      "parents",
      "ultimate parent",
      "ultimate owner",
      "controlling company",
      "who owns the company",
      "which company owns it",
      "is it owned by another company",
      "find the parent",
    ],
  },
  {
    key: "domicile_country",
    name: "Country of Domicile",
    category: "identity",
    owner: "id_lookup",
    idLookupField: "domicile_country",
    aliases: [
      "domicile",
      "country of domicile",
      "legal domicile",
      "country of incorporation",
      "where is it incorporated",
      "which country is it based in",
      "domicile country",
    ],
  },
  {
    key: "ticker",
    name: "Ticker Symbol",
    category: "identity",
    owner: "id_lookup",
    idLookupField: "ticker",
    aliases: [
      "ticker",
      "stock symbol",
      "trading symbol",
      "exchange symbol",
    ],
  },
  {
    key: "entity_id",
    name: "FactSet Entity ID",
    category: "identity",
    owner: "id_lookup",
    idLookupField: "entity_id",
    aliases: ["entity id", "company id", "factset identifier"],
  },
];

/**
 * Requests that mention worksheet or financial vocabulary while asking for
 * something other than one researched value per company row. A request whose
 * nearest neighbour is one of these is declined, which is what lets the router
 * drop the old verb allowlist without handing formatting and modelling work to
 * the research workload.
 */
const DISTRACTOR_TOPICS: readonly WorksheetTopicRow[] = [
  {
    key: "distractor_conversational",
    name: "conversation or greeting",
    category: "conversation",
    owner: "none",
    aliases: [
      "hi",
      "hello",
      "hey",
      "hey there",
      "good morning",
      "good afternoon",
      "thanks",
      "thank you",
      "how are you",
      "what can you do",
      "who are you",
      "help",
    ],
  },
  {
    key: "distractor_build_model",
    name: "build a financial model",
    category: "modelling",
    owner: "none",
    aliases: [
      "build a dcf",
      "build an lbo model",
      "three statement model",
      "merger model",
      "debt schedule",
      "build an operating model",
      "sensitivity analysis",
      "scenario analysis",
    ],
  },
  {
    key: "distractor_build_comps",
    name: "build a comps table",
    category: "modelling",
    owner: "none",
    aliases: [
      "build trading comps",
      "create a comparable companies table",
      "precedent transactions",
      "football field chart",
    ],
  },
  {
    key: "distractor_chart",
    name: "create a chart",
    category: "visualization",
    owner: "none",
    aliases: [
      "make a graph",
      "plot the revenue",
      "chart the share price",
      "build a dashboard",
      "add a pivot table",
      "waterfall chart",
    ],
  },
  {
    key: "distractor_template",
    name: "open a template",
    category: "templates",
    owner: "none",
    aliases: [
      "apply a template",
      "use the standard template",
      "open the model template",
    ],
  },
  {
    key: "distractor_format",
    name: "format cells",
    category: "worksheet editing",
    owner: "none",
    aliases: [
      "bold the revenue column",
      "format the price column",
      "change the font",
      "apply a number format",
      "add borders",
      "highlight these cells",
      "widen the column",
      "make the header bold",
      "color the cells",
    ],
  },
  {
    key: "distractor_structure",
    name: "insert or delete rows and columns",
    category: "worksheet editing",
    owner: "none",
    aliases: [
      "add a column",
      "delete these rows",
      "insert a blank column",
      "rename the sheet",
      "move this to another sheet",
      "clear the contents",
    ],
  },
  {
    key: "distractor_sort_filter",
    name: "sort or filter the rows",
    category: "worksheet editing",
    owner: "none",
    aliases: [
      "sort by revenue",
      "filter the list",
      "rank these companies",
      "order the rows alphabetically",
      "show only the top 10",
    ],
  },
  {
    key: "distractor_aggregate",
    name: "summarize the whole list",
    category: "aggregate analysis",
    owner: "none",
    aliases: [
      "how many companies are in this list",
      "count the rows",
      "average revenue across these companies",
      "total market cap of the list",
      "which of these companies is largest",
      "compare these companies",
      "summarize the list",
    ],
  },
  {
    key: "distractor_formula",
    name: "write a formula",
    category: "formulas",
    owner: "none",
    aliases: [
      "add a sum formula",
      "write a vlookup",
      "fix this formula",
      "explain what this formula does",
      "link these cells",
    ],
  },
  {
    key: "distractor_explain",
    name: "explain a concept",
    category: "explanation",
    owner: "none",
    aliases: [
      "what does ebitda mean",
      "define free cash flow",
      "how is roic calculated",
      "explain the difference between ebit and ebitda",
    ],
  },
  {
    key: "distractor_single_company",
    name: "answer about one named company",
    category: "single company research",
    owner: "none",
    aliases: [
      "what is apple's revenue",
      "tell me about microsoft",
      "how did tesla perform last quarter",
    ],
  },
];

export const WORKSHEET_TOPIC_CATALOG: readonly WorksheetTopicRow[] = [
  ...MARKET_TOPICS,
  ...VALUATION_TOPICS,
  ...PER_SHARE_TOPICS,
  ...SHAREHOLDER_TOPICS,
  ...INCOME_TOPICS,
  ...TAX_TOPICS,
  ...CASH_FLOW_TOPICS,
  ...BALANCE_SHEET_TOPICS,
  ...RATIO_TOPICS,
  ...COMPANY_TOPICS,
  ...IDENTITY_TOPICS,
  ...DISTRACTOR_TOPICS,
];

/** One embeddable phrase belonging to a topic. */
export interface WorksheetTopicProbe {
  /** Stable id: `${topicKey}#${ordinal}`. */
  id: string;
  topicKey: string;
  text: string;
}

/**
 * Flattens the catalog into one probe per name and alias. Scoring a topic as
 * its best-matching probe keeps a short request from being penalized by a
 * topic that happens to carry many aliases.
 */
export function buildWorksheetTopicProbes(
  catalog: readonly WorksheetTopicRow[] = WORKSHEET_TOPIC_CATALOG,
): WorksheetTopicProbe[] {
  const probes: WorksheetTopicProbe[] = [];
  for (const topic of catalog) {
    const phrases = [topic.name, ...topic.aliases];
    const seen = new Set<string>();
    for (const phrase of phrases) {
      const text = phrase.trim();
      if (!text) continue;
      const dedupeKey = text.toLowerCase();
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      probes.push({
        id: `${topic.key}#${probes.length}`,
        topicKey: topic.key,
        text,
      });
    }
  }
  return probes;
}

export function getWorksheetTopic(key: string): WorksheetTopicRow | undefined {
  return WORKSHEET_TOPIC_CATALOG.find((topic) => topic.key === key);
}
